<template>
    <ChartPortlet
        ref="chartPortlet"
        :title="i18n('dashboard.salesByPaymentMethodChart.chartTitle')"
        :chartHeight="fullChartHeight"
        :useDescription="false"
    />
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DashboardDao } from "@/data/dao/service/DashboardDao";
    import type { DashboardStatsPeriod } from "@/data/model/Enums";
    import { i18n } from "@/portal/Trans";
    import { logError } from "@/shared/logging/errors";
    import { Chart } from "chart.js/auto";
    import { computed, nextTick, onMounted, ref, watch } from "vue";

    import ChartPortlet from "@/portal/components/dashboard/ChartPortlet.vue";

    import { CHART_COLORS } from "./Utils";
    import {
        barStyle,
        categoriesScaleRef,
        defaultBarChartLayout,
        defaultTooltip,
        fullChartHeight,
        moneyScale,
        visibleLegend,
    } from "./sharedChartUtils";

    const dao = Factory.getInstance<DashboardDao>(DashboardDao);

    const props = defineProps<{
        locationId?: string;
        period?: DashboardStatsPeriod;
        selectedDate: Date | undefined;
    }>();

    let chart: Chart;

    const labels = ref<string[]>([]);
    const isInitialized = ref(false);
    const chartPortlet = ref<InstanceType<typeof ChartPortlet> | null>(null);
    const canvas = computed(() => chartPortlet.value?.canvasElement);
    const paramsLoaded = computed(() => props.locationId !== undefined && props.period !== undefined);

    // create chart if all params are provided and is first init or init after not all params were provided
    onMounted(initializeChart);
    watch(paramsLoaded, initializeChart);

    // reload data if any prop is changed
    watch(() => props.locationId, refreshData);
    watch(() => props.period, refreshData);
    watch(() => props.selectedDate, refreshData);

    async function initializeChart() {
        if (!paramsLoaded.value) return;

        await nextTick();
        createChart();
        loadData();
    }

    function createChart() {
        const ctx = canvas.value!.getContext("2d")!;

        chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: [],
                datasets: [
                    {
                        label: i18n("dashboard.salesByPaymentMethodChart.onlineBrowser"),
                        ...barStyle(CHART_COLORS.darkGreen, CHART_COLORS.lightGreen),
                        data: [],
                    },
                    {
                        label: i18n("dashboard.salesByPaymentMethodChart.onlinePaymentDevice"),
                        ...barStyle(CHART_COLORS.darkOrange, CHART_COLORS.lightOrange),
                        data: [],
                    },
                    {
                        label: i18n("dashboard.salesByPaymentMethodChart.offline"),
                        ...barStyle(CHART_COLORS.darkBlue, CHART_COLORS.lightBlue),
                        data: [],
                    },
                    {
                        label: i18n("dashboard.salesByPaymentMethodChart.nre"),
                        ...barStyle(CHART_COLORS.darkPink, CHART_COLORS.lightPink),
                        data: [],
                    },
                ],
            },
            options: {
                skipNull: true,
                scales: {
                    x: categoriesScaleRef(labels),
                    y: moneyScale(),
                },
                ...defaultBarChartLayout,
                plugins: {
                    legend: visibleLegend,
                    tooltip: defaultTooltip(),
                },
            },
        });

        isInitialized.value = true;
    }

    async function refreshData() {
        if (!isInitialized.value) return;

        loadData();
    }

    async function loadData() {
        try {
            console.log(props.selectedDate)
            const summary = await dao.getSalesByPaymentMethodSummary(props.locationId!, props.period!,  props.selectedDate );

            const onlineBrowserPms = summary.onlineBrowserPaymentMethodSummaries;
            const onlinePaymentDevicePms = summary.onlinePaymentDevicePaymentMethodSummaries;
            const offlinePms = summary.offlinePaymentMethodSummaries;
            const nrePms = summary.nrePaymentMethodSummaries;

            labels.value = [
                ...onlineBrowserPms.map((x) => x.paymentMethodName),
                ...onlinePaymentDevicePms.map((x) => x.paymentMethodName),
                ...offlinePms.map((x) => x.paymentMethodName),
                ...nrePms.map((x) => x.paymentMethodName),
            ];
            chart.data.labels = labels.value;

            // map "Online - browser" payment methods
            chart.data.datasets[0].data = [...onlineBrowserPms.map((x) => x.totalAmount)];

            // map "Online - payment device" payment methods
            // prepend data with nulls - for each of previous payment methods
            chart.data.datasets[1].data = [
                ...onlineBrowserPms.map(() => null),
                ...onlinePaymentDevicePms.map((x) => x.totalAmount),
            ];

            // map "Offline - browser" payment methods
            // prepend data with nulls - for each of previous payment methods
            chart.data.datasets[2].data = [
                ...onlineBrowserPms.map(() => null),
                ...onlinePaymentDevicePms.map(() => null),
                ...offlinePms.map((x) => x.totalAmount),
            ];

            // map "Not revenue-effective" payment methods
            // prepend data with nulls - for each of previous payment methods
            chart.data.datasets[3].data = [
                ...onlineBrowserPms.map(() => null),
                ...onlinePaymentDevicePms.map(() => null),
                ...offlinePms.map(() => null),
                ...nrePms.map((x) => x.totalAmount),
            ];

            chart.update();
        } catch (e) {
            logError(e);
        }
    }
</script>
